import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Home, ListTodo, Menu, Plus, X } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import {
  AgentModeSwitcher,
  useAgentMode,
  useVoiceAgent,
  useWriteAgent,
  VoiceAgentButton,
  VoiceAgentSheet,
  WriteAgentSheet,
} from "../voice-agent";
import type { AgentMode } from "../voice-agent";
import { MobileMegaMenu } from "./MobileMegaMenu";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const [agentMode, setAgentMode] = useAgentMode();
  const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const voiceAgent = useVoiceAgent();
  const writeAgent = useWriteAgent();

  const handleModeChange = (m: AgentMode) => {
    voiceAgent.reset();
    writeAgent.reset();
    setAgentMode(m);
  };

  const isDashboard = !!matchPath("/", location.pathname);
  const isTasks = !!matchPath("/tasks/*", location.pathname);

  return (
    <>
      <nav
        aria-label={translate("crm.navigation.label")}
        className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none px-3"
        style={{
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        }}
      >
        <div
          className={cn(
            "pointer-events-auto flex items-end gap-0.5 px-2 py-1.5",
            "rounded-full border border-border/80 shadow-lg",
            "bg-secondary/90 backdrop-blur-md supports-[backdrop-filter]:bg-secondary/80",
          )}
        >
          <PillNavButton
            href="/"
            Icon={Home}
            label={translate("ra.page.dashboard")}
            isActive={isDashboard}
          />

          <CreateButton />

          <Button
            type="button"
            variant="default"
            size="icon"
            aria-label={translate("crm.mobile_nav.open_menu", {
              _: "Open menu",
            })}
            aria-expanded={megaMenuOpen}
            className={cn(
              "size-14 rounded-full -translate-y-3 mx-0.5 shrink-0",
              "shadow-md border-4 border-secondary",
              megaMenuOpen && "bg-muted text-foreground",
            )}
            onClick={() => setMegaMenuOpen((open) => !open)}
          >
            {megaMenuOpen ? (
              <X className="size-6" />
            ) : (
              <Menu className="size-6" />
            )}
          </Button>

          <PillNavButton
            href="/tasks"
            Icon={ListTodo}
            label={translate("crm.mobile_nav.my_tasks", {
              _: "My tasks",
            })}
            isActive={isTasks}
          />

          <AgentModeSwitcher
            open={agentPopoverOpen}
            onOpenChange={setAgentPopoverOpen}
            mode={agentMode}
            onModeChange={handleModeChange}
          >
            <VoiceAgentButton
              mode={agentMode}
              isActive={
                agentMode === "voice"
                  ? voiceAgent.state === "recording"
                  : writeAgent.state === "open"
              }
              onPress={
                agentMode === "voice"
                  ? voiceAgent.startRecording
                  : writeAgent.openSheet
              }
              onLongPress={() => setAgentPopoverOpen(true)}
            />
          </AgentModeSwitcher>
        </div>
      </nav>

      <MobileMegaMenu open={megaMenuOpen} onOpenChange={setMegaMenuOpen} />

      <VoiceAgentSheet
        isOpen={voiceAgent.isSheetOpen}
        state={voiceAgent.state}
        transcript={voiceAgent.transcript}
        response={voiceAgent.response}
        error={voiceAgent.error}
        elapsedSeconds={voiceAgent.elapsedSeconds}
        onStop={voiceAgent.stopRecording}
        onNewRequest={voiceAgent.startRecording}
        onClose={voiceAgent.reset}
      />
      <WriteAgentSheet
        isOpen={writeAgent.isSheetOpen}
        state={writeAgent.state}
        inputText={writeAgent.inputText}
        response={writeAgent.response}
        error={writeAgent.error}
        elapsedSeconds={writeAgent.elapsedSeconds}
        onInputChange={writeAgent.setInputText}
        onSubmit={writeAgent.submit}
        onNewRequest={() => {
          writeAgent.reset();
          writeAgent.openSheet();
        }}
        onClose={writeAgent.reset}
      />
    </>
  );
};

const PillNavButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-0.5 h-auto py-2 px-2 rounded-2xl min-w-[3.25rem]",
      isActive ? "text-foreground" : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-5" />
      <span className="text-[0.6rem] font-medium leading-tight text-center max-w-[4rem] truncate">
        {label}
      </span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex-col gap-0.5 h-auto py-2 px-2 rounded-2xl min-w-[3.25rem] text-muted-foreground"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-5" />
            <span className="text-[0.6rem] font-medium">
              {translate("ra.action.create")}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="center" className="mb-2">
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setNoteCreateOpen(true)}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setTaskCreateOpen(true)}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
